import { emptyFragment } from '@execonline-inc/execonline-ui';
import clsx from 'clsx';
import { fromEmpty } from 'maybeasy';
import { observer } from 'mobx-react';
import { MouseEvent, useContext } from 'react';
import { EventsStoreContext, findEvent } from '../../../EventsStore/EventsStoreContext';
import { EventResource } from '../../../EventsStore/Types';
import { L, TPlainTextKey } from '../../../Translations';
import EventDetails from '../../EventDetails';
import { useOpenable } from '../../Openable';
import { programCardBoxClassName, programCardBoxHoverClassName, ProgramCardFooterGroup } from '.';

export interface UpcomingSessionItem {
  // The event id, used to find the event's details in the participant's events list.
  id: number;
  startAt: Date;
}

interface Props {
  label: TPlainTextKey;
  // The next few sessions, soonest first.
  sessions: ReadonlyArray<UpcomingSessionItem>;
  // The number of sessions after these, shown as "+N" on the last one.
  moreCount: number;
}

interface SessionContentProps {
  startAt: Date;
  moreCount: number;
}

function SessionContent({ startAt, moreCount }: SessionContentProps) {
  return (
    <>
      <div className="w-10 text-center">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-600">
          <L localizeable={startAt} format="short-month" />
        </div>
        <div className="text-lg font-semibold leading-tight text-gray-900">
          <L localizeable={startAt} format="day-of-month" />
        </div>
      </div>
      <span className="text-sm font-semibold text-gray-900">
        <L localizeable={startAt} format="time-of-day" />
      </span>
      {moreCount > 0 && (
        <span
          className="rounded-full bg-logo-100 px-2 text-xs font-semibold text-primary-700"
          data-test-more-sessions={moreCount}
        >
          +{moreCount}
        </span>
      )}
    </>
  );
}

interface SessionWithDetailsProps extends SessionContentProps {
  event: EventResource;
}

// Opens the same event details as the events list. The upcoming program card is one big
// link, so the button prevents the default (the link does not follow a prevented click)
// and clicks inside the details, which bubble through the portal, stop before the link.
function SessionWithDetails({ event, startAt, moreCount }: SessionWithDetailsProps) {
  const { openableState, open, close } = useOpenable();

  const openDetails = (e: MouseEvent) => {
    e.preventDefault();
    open();
  };

  return (
    <>
      <button
        type="button"
        onClick={openDetails}
        className={clsx(programCardBoxClassName, programCardBoxHoverClassName)}
        aria-label={`View details for ${event.payload.title}`}
        data-intercom-target="dashboard-upcoming-session-button"
        data-test-upcoming-session={event.payload.id}
      >
        <SessionContent startAt={startAt} moreCount={moreCount} />
      </button>
      <span
        role="presentation"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <EventDetails modalState={openableState} close={close} event={event} />
      </span>
    </>
  );
}

// The events list holds only the next 45 days, so a later session shows without details.
const Session = observer(
  ({ session, moreCount }: { session: UpcomingSessionItem; moreCount: number }) =>
    useContext(EventsStoreContext)
      .andThen((eventsStore) => findEvent(eventsStore, session.id))
      .map((event) => (
        <SessionWithDetails event={event} startAt={session.startAt} moreCount={moreCount} />
      ))
      .getOrElse(() => (
        <div className={programCardBoxClassName}>
          <SessionContent startAt={session.startAt} moreCount={moreCount} />
        </div>
      )),
);

// A group in the card footer. With no upcoming sessions there is nothing to show.
function UpcomingSessions({ label, sessions, moreCount }: Props) {
  return fromEmpty([...sessions])
    .map((items) => (
      <ProgramCardFooterGroup label={label}>
        <ul className="m-0 flex list-none flex-wrap gap-3 p-0" data-test-upcoming-sessions={label}>
          {items.map((session, i) => (
            <li key={session.id}>
              <Session session={session} moreCount={i === items.length - 1 ? moreCount : 0} />
            </li>
          ))}
        </ul>
      </ProgramCardFooterGroup>
    ))
    .getOrElse(emptyFragment);
}

export default UpcomingSessions;
